import FindReplaceIcon from "@mui/icons-material/FindReplace";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import Document from "documents/document";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import DocumentDownloadControl from "documents/downloads/_views/document-download-control";
import useDocumentDownloadVerdicts from "documents/downloads/use-document-download-verdicts";
import { DocumentFileType } from "documents/document-file-type";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import React from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { Link as RouterLink } from "react-router";
import { workPaneProseInset } from "work/_views/work-pane-layout";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import WorkDocument from "work/work-agreements/contracts/work-document";

const DocumentList = styled(List)(({ theme }) => ({
  "&.MuiList-root": {
    marginLeft: 0,
  },
  // The heading and the rows stand in the prose inset rather than Material's fixed 16px gutter, so
  // they line up with the actions row above the tab at every width: 16px where the tab's row runs
  // flush to the pane, none where the pane's own gutter already puts them there. See
  // `workPaneProseInset`.
  "& > .MuiListSubheader-root, & > .MuiListItem-root": workPaneProseInset(theme),
  maxWidth: "42rem",
  /**
   * A floor that yields once there is no room for it.
   *
   * A flat `32rem` is 512px, which is wider than a phone — and wider than the archived Conflicts and
   * Policies tabs are given on one — so the list overflowed its own tab and a reader had to scroll
   * sideways to see a document's date. `min()` keeps the floor wherever the room exists and drops to
   * the room available where it does not, which is the same thing the row's own columns do.
   */
  minWidth: "min(32rem, 100%)",
  width: "100%",
}));
const ListSubhead = styled(ListSubheader)(() => ({
  backgroundColor: "#FFF",
  color: "#000",
  display: "flex",
  fontSize: "1.4em",
  justifyContent: "space-between",
  paddingRight: 0,
  whiteSpace: "nowrap",
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const DocumentIcon = styled(ListItemAvatar)(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: "2rem",
  maxWidth: "2rem",
}));

type DocumentListReadonlyProps = {
  className?: string;
  documents?: WorkDocument[];
  documentType: WorkDocumentType;
  downloadingFileId: Guid | null;
  conflictsCheckWaived?: boolean;
  /**
   * Where replacing a document across every agreement that holds it starts, when the reader may do
   * that (#6257). Left off, the row offers only Download.
   */
  replaceLinkFor?: (document: WorkDocument) => string;
  onDownload: (documentId: Guid) => void;
};

export default function DocumentListReadonly(props: Readonly<DocumentListReadonlyProps>) {
  const {
    className,
    documents,
    documentType,
    downloadingFileId,
    conflictsCheckWaived,
    replaceLinkFor,
    onDownload,
  } = props;

  // A row is the work's projection of a document, which carries no verdict of its own. The documents
  // API's copy says whether this reader's download would be refused, and why (#6992).
  const downloadVerdicts = useDocumentDownloadVerdicts(
    conflictsCheckWaived ? [] : (documents ?? []).map((document) => document.id),
  );

  function getPolicyHeader(): string {
    return documentType === WorkDocumentType.VendorPolicy ? "Vendor Policies" : "Client Policies";
  }

  function renderActionButtons(document: WorkDocument) {
    const isUnavailable = document.isAvailable === false;

    return (
      <>
        {replaceLinkFor && !isUnavailable && (
          <Tooltip title="Replace this policy on every agreement that holds it">
            <IconButton
              component={RouterLink}
              to={replaceLinkFor(document)}
              aria-label={`Replace ${document.name.value} across agreements`}
              size="large"
            >
              <FindReplaceIcon color="primary" fontSize="medium" />
            </IconButton>
          </Tooltip>
        )}
        <DocumentDownloadControl
          document={downloadVerdicts.get(document.id.value)}
          name={document.name.value}
          onDownload={() => onDownload(document.id)}
          downloading={downloadingFileId?.isEqualTo(document.id)}
          disabled={downloadingFileId !== null || isUnavailable}
          disabledReason={isUnavailable ? "Document unavailable" : undefined}
          edge="end"
          size="large"
        />
      </>
    );
  }

  return (
    <DocumentList disablePadding className={className}>
      <ListSubhead>
        {documentType === WorkDocumentType.Conflicts && (
          <span>
            Your Conflicts Checks
            <Tooltip
              disableFocusListener
              title="Conflicts document(s) establish potential conflicts checks required by participating clients/vendors"
              placement="bottom"
              arrow
            >
              <IconButton size="large" edge="end">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </span>
        )}
        {(documentType === WorkDocumentType.ClientPolicy || documentType === WorkDocumentType.VendorPolicy) && (
          <span>
            {getPolicyHeader()}
            <Tooltip
              disableFocusListener
              title="Policy document(s) establish the policies required by participating clients/vendors"
              placement="bottom"
              arrow
            >
              <IconButton size="large" edge="end">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </span>
        )}
      </ListSubhead>
      {!conflictsCheckWaived && (!documents || documents.length < 1) && (
        <ListItem key="noDocuments">
          {/* The icon spacer keeps the empty state lined up with the document rows rather than the
                section heading. */}
          <DocumentIcon />
          <NoRowsPlaceholder>No Documents</NoRowsPlaceholder>
        </ListItem>
      )}
      {!conflictsCheckWaived &&
        documents
          ?.filter((document) => !(document instanceof UnavailableDocument))
          .map((document: WorkDocument | UnavailableDocument) => {
            const availableDocument = document as WorkDocument;
            const secondaryText = availableDocument.isAvailable === false
              ? "Unavailable"
              : availableDocument.created?.format("MM/DD/YY hh:mm A");
            return (
              <ListItem key={document.id.value as React.Key} secondaryAction={renderActionButtons(availableDocument)}>
                <DocumentIcon>
                  <FileIcon {...getFileTypeIconProps(availableDocument)} />
                </DocumentIcon>
                <ListItemText
                  primary={availableDocument.name?.value}
                  secondary={
                    <>
                      {secondaryText}
                      <DocumentProcessingState
                        status={downloadVerdicts.get(availableDocument.id.value)?.classificationStatus}
                        scanStatus={downloadVerdicts.get(availableDocument.id.value)?.scanStatus}
                      />
                    </>
                  }
                  // Room for the row's actions, which sit over the end of the row: one or two buttons.
                  sx={{ marginRight: replaceLinkFor ? 12 : 6 }}
                  slotProps={{
                    primary: {
                      sx: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }
                    },
                  }}
                />
              </ListItem>
            );
          })}
    </DocumentList>
  );
}

/** Only ever called for a row the list kept, and the list filters `UnavailableDocument` out. */
function getFileTypeIconProps(document: Document) {
  const extension = enumKeyOf(DocumentFileType, document.fileType).toLocaleLowerCase();

  return {
    extension,
    ...defaultStyles[extension as keyof typeof defaultStyles],
  };
}
